import CropFreeIcon from "@mui/icons-material/CropFree";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";

import Button from "../../../Buttons/Button";
import IconButton from "../../../Buttons/IconButton";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  ADD_TO_FAVORITES,
  MODAL_BATCH_IMAGE_EDIT_EXPAND,
  REMOVE_FROM_FAVORITES,
} from "../../../../i18n/keysTranslations";
import { PRIMARY_COLOR_TEXT } from "../../../../utils/colors";
import { getCompetitorAdExternalUrlFromItem } from "../../../../services/competitorAdExternalUrls";
import { gallerySecondaryToolbarIconSx } from "../../../Gallery/galleryIconButtonStyles";

const sxActionStrip = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  width: "100%",
  flexShrink: 0,
  px: 1,
  py: 0.75,
  containerType: "inline-size",
  "@container (max-width: 280px)": {
    px: 0.5,
  },
};

const sxGalleryStripSurface = {
  backgroundColor: "#ffffff",
  backdropFilter: "none",
  WebkitBackdropFilter: "none",
};

const sxGalleryActionRow = {
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 1,
  width: "100%",
  minWidth: 0,
  overflow: "visible",
  "@container (max-width: 280px)": {
    gap: 0.5,
  },
};

const sxGalleryActionCluster = {
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: 0.5,
  flexShrink: 0,
  overflow: "visible",
};

const sxGenerateButton = {
  height: { xs: 44, sm: 40 },
  minWidth: 0,
  width: "100%",
  borderRadius: "10px !important",
  textTransform: "none",
  fontSize: { xs: 13, sm: 14 },
  fontWeight: 500,
  boxShadow: "none",
  whiteSpace: "normal",
};

const sxGalleryActionIconToolbar = (theme) =>
  gallerySecondaryToolbarIconSx({
    theme,
    iconSvgFontSize: 15,
    buttonSize: 27,
  });

const sxGalleryActionStripCompact = {
  "@container (max-width: 260px)": {
    gap: 0.25,
    "& .MuiIconButton-root": {
      width: 26,
      height: 26,
      minWidth: 26,
      "& .MuiSvgIcon-root": {
        fontSize: 14,
      },
    },
  },
  "@container (max-width: 220px)": {
    gap: 0.125,
    "& .MuiIconButton-root": {
      width: 24,
      height: 24,
      minWidth: 24,
      "& .MuiSvgIcon-root": {
        fontSize: 13,
      },
    },
  },
  "@container (max-width: 180px)": {
    gap: 0,
    "& .MuiIconButton-root": {
      width: 22,
      height: 22,
      minWidth: 22,
      "& .MuiSvgIcon-root": {
        fontSize: 12,
      },
    },
  },
};

const CompetitorItemActions = ({
  item,
  isFavorite = false,
  hideFavoriteButton = false,
  showDirectAddButton = false,
  directAddLabel,
  isDirectAddLoading = false,
  isDirectAddDisabled = false,
  onPreview = () => {},
  onToggleFavorite = () => {},
  onDirectGenerate = () => {},
  onOpenExternalAd,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();

  const competitorAdExternalUrl = getCompetitorAdExternalUrlFromItem(item);
  const favoriteLabel = t(
    isFavorite ? REMOVE_FROM_FAVORITES : ADD_TO_FAVORITES
  );

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={sxGalleryStripSurface}
    >
      <Box
        sx={{
          ...sxActionStrip,
          minHeight: 48,
          borderTop: "1px solid rgba(0, 0, 0, 0.08)",
        }}
      >
        <Box sx={sxGalleryActionRow}>
          <Box
            sx={{
              ...sxGalleryActionCluster,
              ...sxGalleryActionIconToolbar(theme),
              ...sxGalleryActionStripCompact,
            }}
          >
            <IconButton
              onClick={onPreview}
              type="icon"
              edge={false}
              tooltipText={MODAL_BATCH_IMAGE_EDIT_EXPAND}
            >
              <CropFreeIcon
                sx={{ color: PRIMARY_COLOR_TEXT, fill: PRIMARY_COLOR_TEXT }}
              />
            </IconButton>

            {hideFavoriteButton ? null : (
              <Tooltip title={favoriteLabel} arrow placement="top">
                <span>
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleFavorite(event);
                    }}
                    type="icon"
                    edge={false}
                    aria-label={favoriteLabel}
                  >
                    {isFavorite ? (
                      <FavoriteIcon sx={{ color: "#e53935", fill: "#e53935" }} />
                    ) : (
                      <FavoriteBorderIcon
                        sx={{
                          color: PRIMARY_COLOR_TEXT,
                          fill: PRIMARY_COLOR_TEXT,
                        }}
                      />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {competitorAdExternalUrl ? (
              <IconButton
                type="icon"
                edge={false}
                onClick={(event) => {
                  event.stopPropagation();
                  if (onOpenExternalAd) {
                    onOpenExternalAd(event);
                    return;
                  }
                  window.open(
                    competitorAdExternalUrl,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
              >
                <VisibilityIcon
                  sx={{
                    color: PRIMARY_COLOR_TEXT,
                    fill: PRIMARY_COLOR_TEXT,
                  }}
                />
              </IconButton>
            ) : null}
          </Box>
        </Box>
      </Box>

      {showDirectAddButton ? (
        <Box
          className="gallery-direct-add-button"
          sx={{
            px: 1,
            pb: 1,
            pt: 0.5,
            bgcolor: "#ffffff",
          }}
        >
          <Button
            color="secondary"
            fullWidth
            onClick={onDirectGenerate}
            loading={isDirectAddLoading}
            disabled={isDirectAddDisabled}
            ariaLabel={directAddLabel}
            sx={sxGenerateButton}
          >
            {directAddLabel}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default CompetitorItemActions;
